import { invalidateProblemCaches, invalidateQBCache, problemUrl, qbFetch, skillProblemsUrl } from "./qb-cache";
import type { Problem, Skill } from "./types";

export type ProblemTarget = { id: string; skillId: string };

/** Server caps limit at 200. */
export const PROBLEM_PAGE_SIZE = 200;

/** Deletes landed per skill; a page requested before one has a stale offset. */
export type SkillGenerations = Map<string, number>;

const genOf = (gens: SkillGenerations, skillId: string) => gens.get(skillId) ?? 0;

export const bumpGeneration = (gens: SkillGenerations, skillId: string) => {
  gens.set(skillId, genOf(gens, skillId) + 1);
};

/** Each delete removes one loaded row, so a page a delete overtook is asked for again that many rows earlier. */
export async function fetchSkillPage(skillId: string, offset: number, gens: SkillGenerations) {
  for (;;) {
    const gen = genOf(gens, skillId);
    const data = await qbFetch<{ items: Problem[]; total: number }>(
      `${skillProblemsUrl(skillId)}?limit=${PROBLEM_PAGE_SIZE}&offset=${offset}`,
      { ttl: 60_000 },
    );
    const deletes = genOf(gens, skillId) - gen;
    if (deletes === 0) return data;
    invalidateQBCache(skillProblemsUrl(skillId));
    offset = Math.max(0, offset - deletes);
  }
}

/** Studio has already dropped the row, so `onDeleted` runs for `already_deleted` too. */
export async function deleteProblem(
  target: ProblemTarget,
  onDeleted: (target: ProblemTarget) => void,
): Promise<boolean> {
  const body = await qbFetch<{ already_deleted: boolean }>(problemUrl(target.id), { method: "DELETE" });
  invalidateProblemCaches(target.id, target.skillId);
  onDeleted(target);
  return body.already_deleted;
}

export async function restoreProblem(
  target: ProblemTarget,
  onRestored: (target: ProblemTarget) => void,
): Promise<void> {
  await qbFetch(`${problemUrl(target.id)}/restore`, { method: "POST" });
  invalidateProblemCaches(target.id, target.skillId);
  onRestored(target);
}

/** A deleted row and where it sat, so a restore can put it back without a refetch. */
export type RemovedRow = { skillId: string; index: number; problem: Problem };

export const findRow = (lists: Map<string, Problem[]>, { id, skillId }: ProblemTarget): RemovedRow | null => {
  const list = lists.get(skillId) ?? [];
  const index = list.findIndex((p) => p.id === id);
  return index < 0 ? null : { skillId, index, problem: list[index]! };
};

export const withoutProblem = (lists: Map<string, Problem[]>, { id, skillId }: ProblemTarget) => {
  const list = lists.get(skillId);
  return list ? new Map(lists).set(skillId, list.filter((p) => p.id !== id)) : lists;
};

export const withRow = (lists: Map<string, Problem[]>, { skillId, index, problem }: RemovedRow) => {
  const list = lists.get(skillId);
  return list ? new Map(lists).set(skillId, list.toSpliced(index, 0, problem)) : lists;
};

export const adjustTotal = (totals: Map<string, number>, skillId: string, delta: number) => {
  const total = totals.get(skillId);
  return total === undefined ? totals : new Map(totals).set(skillId, Math.max(0, total + delta));
};

export const adjustSkillCount = <T extends { totalProblems: number; skills: Skill[] }>(
  state: T,
  skillId: string,
  delta: number,
): T => ({
  ...state,
  totalProblems: Math.max(0, state.totalProblems + delta),
  skills: state.skills.map((s) =>
    s.id === skillId ? { ...s, problem_count: Math.max(0, s.problem_count + delta) } : s,
  ),
});
